import React, { useState, useEffect } from 'react';
import { formatCurrency, formatFileSize } from '../utils/formatCurrency';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

const InvoiceViewModal = ({ invoice, onClose }) => {
  const [zoom, setZoom] = useState(0.25);

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!invoice) return null;

  const isPDF = invoice.mimeType === 'application/pdf';
  const fileUrl = invoice.filename
    ? `${process.env.REACT_APP_API_URL.replace('/api', '')}/uploads/${invoice.filename}`
    : null;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = invoice.originalName || invoice.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const zoomIn = () => setZoom(z => Math.min(MAX_ZOOM, parseFloat((z + ZOOM_STEP).toFixed(2))));
  const zoomOut = () => setZoom(z => Math.max(MIN_ZOOM, parseFloat((z - ZOOM_STEP).toFixed(2))));
  const resetZoom = () => setZoom(0.25);

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
  };

  // ── No-attachment: compact centered modal ────────────────────────────────
  if (!fileUrl) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px', width: '90vw' }}>
          <div className="flex-between mb-lg">
            <h3 style={{ margin: 0 }}>{invoice.vendorName}</h3>
            <button onClick={onClose} className="btn btn-secondary btn-sm">✕</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            {/* Amount */}
            <div>
              <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Amount</div>
              {invoice.gstIncluded ? (
                <div style={{ fontSize: 'var(--font-size-sm)' }}>
                  <div style={{ color: 'var(--color-secondary)' }}>Base: {formatCurrency(parseFloat(invoice.amount) / 1.18)}</div>
                  <div style={{ color: 'var(--color-secondary)' }}>GST (18%): {formatCurrency(parseFloat(invoice.amount) - parseFloat(invoice.amount) / 1.18)}</div>
                  <div style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', fontSize: 'var(--font-size-md)' }}>
                    Total: {formatCurrency(invoice.amount)}
                  </div>
                </div>
              ) : (
                <div style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', fontSize: 'var(--font-size-md)' }}>
                  {formatCurrency(invoice.amount)}
                </div>
              )}
            </div>

            {/* Date */}
            <div>
              <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Date</div>
              <div style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                {new Date(invoice.invoiceDate).toLocaleDateString('en-GB')}
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div>
                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Notes</div>
                <div style={{ fontSize: 'var(--font-size-sm)' }}>{invoice.notes}</div>
              </div>
            )}

            {/* Additional Attachments */}
            {invoice.attachments && invoice.attachments.length > 0 && (
              <div>
                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Attachments</div>
                {invoice.attachments.map(att => {
                  const attUrl = `${process.env.REACT_APP_API_URL.replace('/api', '')}/uploads/${att.filename}`;
                  return (
                    <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--spacing-xs) var(--spacing-sm)', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', marginBottom: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '8px' }}>
                        📎 {att.originalName}
                        {att.fileSize ? <span style={{ marginLeft: '6px', color: 'var(--color-gray-400)', fontSize: '11px' }}>({formatFileSize(att.fileSize)})</span> : null}
                      </span>
                      <a href={attUrl} download={att.originalName} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 8px', flexShrink: 0 }}>↓</a>
                    </div>
                  );
                })}
              </div>
            )}

            <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-end' }}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  // ── With attachment: full-screen viewer ───────────────────────────────────
  return (
    <div className="modal-overlay" onClick={onClose} style={{ padding: '12px', alignItems: 'stretch', justifyContent: 'stretch' }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-white)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-xl)',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
        }}
      >
        {/* Header */}
        <div className="flex-between" style={{ padding: 'var(--spacing-md) var(--spacing-lg)', borderBottom: '1px solid var(--color-gray-200)', flexShrink: 0 }}>
          <div>
            <h3 style={{ margin: 0 }}>{invoice.vendorName}</h3>
            {invoice.originalName && (
              <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginTop: '2px' }}>
                {invoice.originalName}
                {invoice.fileSize ? <span style={{ marginLeft: '6px', color: 'var(--color-gray-400)' }}>({formatFileSize(invoice.fileSize)})</span> : null}
              </div>
            )}
          </div>
          <button onClick={onClose} className="btn btn-secondary btn-sm">✕</button>
        </div>

        {/* Body — two columns */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* Left panel — invoice details */}
          <div style={{ width: '300px', flexShrink: 0, borderRight: '1px solid var(--color-gray-200)', overflowY: 'auto', padding: 'var(--spacing-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>

            {/* Amount */}
            <div>
              <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Amount</div>
              {invoice.gstIncluded ? (
                <div style={{ fontSize: 'var(--font-size-sm)' }}>
                  <div style={{ color: 'var(--color-secondary)' }}>Base: {formatCurrency(parseFloat(invoice.amount) / 1.18)}</div>
                  <div style={{ color: 'var(--color-secondary)' }}>GST (18%): {formatCurrency(parseFloat(invoice.amount) - parseFloat(invoice.amount) / 1.18)}</div>
                  <div style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', fontSize: 'var(--font-size-md)' }}>
                    Total: {formatCurrency(invoice.amount)}
                  </div>
                </div>
              ) : (
                <div style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', fontSize: 'var(--font-size-md)' }}>
                  {formatCurrency(invoice.amount)}
                </div>
              )}
            </div>

            {/* Date */}
            <div>
              <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Date</div>
              <div style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                {new Date(invoice.invoiceDate).toLocaleDateString('en-GB')}
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div>
                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Notes</div>
                <div style={{ fontSize: 'var(--font-size-sm)' }}>{invoice.notes}</div>
              </div>
            )}

            {/* Additional Attachments */}
            {invoice.attachments && invoice.attachments.length > 0 && (
              <div>
                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Attachments</div>
                {invoice.attachments.map(att => {
                  const attUrl = `${process.env.REACT_APP_API_URL.replace('/api', '')}/uploads/${att.filename}`;
                  return (
                    <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--spacing-xs) var(--spacing-sm)', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', marginBottom: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '8px' }}>
                        📎 {att.originalName}
                        {att.fileSize ? <span style={{ marginLeft: '6px', color: 'var(--color-gray-400)', fontSize: '11px' }}>({formatFileSize(att.fileSize)})</span> : null}
                      </span>
                      <a href={attUrl} download={att.originalName} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 8px', flexShrink: 0 }}>↓</a>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Spacer + Actions */}
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
              <button onClick={handleDownload} className="btn btn-primary btn-sm">Download</button>
              <button onClick={onClose} className="btn btn-secondary btn-sm">Close</button>
            </div>
          </div>

          {/* Right panel — image/PDF viewer */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'var(--color-gray-50)' }}>
            {/* Zoom controls */}
            {!isPDF && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', padding: '6px var(--spacing-md)', borderBottom: '1px solid var(--color-gray-200)', backgroundColor: 'var(--color-white)', flexShrink: 0 }}>
                <button onClick={zoomOut} className="btn btn-secondary btn-sm" disabled={zoom <= MIN_ZOOM} style={{ minWidth: '28px', fontWeight: 'bold' }}>−</button>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', minWidth: '44px', textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
                <button onClick={zoomIn} className="btn btn-secondary btn-sm" disabled={zoom >= MAX_ZOOM} style={{ minWidth: '28px', fontWeight: 'bold' }}>+</button>
                {zoom !== 0.25 && (
                  <button onClick={resetZoom} className="btn btn-secondary btn-sm" style={{ fontSize: '11px' }}>Reset</button>
                )}
              </div>
            )}

            {/* Viewer */}
            <div
              onWheel={!isPDF ? handleWheel : undefined}
              style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-md)', display: 'flex', alignItems: zoom <= 0.5 ? 'center' : 'flex-start', justifyContent: zoom <= 0.5 ? 'center' : 'flex-start' }}
            >
              {isPDF ? (
                <iframe
                  src={fileUrl}
                  title="Invoice PDF"
                  style={{ width: '100%', height: '100%', border: 'none', borderRadius: 'var(--radius-sm)' }}
                />
              ) : (
                <img
                  src={fileUrl}
                  alt={invoice.vendorName}
                  onClick={zoom < MAX_ZOOM ? zoomIn : undefined}
                  style={{
                    width: `${zoom * 100}%`,
                    maxWidth: 'none',
                    height: 'auto',
                    borderRadius: 'var(--radius-sm)',
                    display: 'block',
                    transition: 'width 0.15s ease',
                    cursor: zoom < MAX_ZOOM ? 'zoom-in' : 'default'
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceViewModal;
